import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BordEmailPreview,
  BordGroupedComboBox,
  BordIncidentClosureList,
  BordIncidentDatesForm,
  BordLateralModal,
  BordOneToneIcon,
  BordTooltip
} from 'bord-design-system';
import {
  ACTIVE_ONLY_INCIDENT_GROUPS,
  CANCEL_INCIDENT_CODES,
  IBordClosableIncident,
  ICloseIncidentsFailed,
  IIncidentDatesFormValue,
  IIncidentReason,
  IIncidentReasonsGroupedByType,
  INCIDENT_MODES,
  INCIDENT_REASON_CODES,
  INCIDENT_REASON_TYPE_LABELS,
  INCIDENT_SOURCE_REFERENCE_ID,
  INCIDENT_STATUS_ID,
  INCIDENT_TYPE_ID,
  IOpenIncident,
  IServiceIncidentSidebar,
  PREDECESSOR_CODES_BY_REASON
} from '../../types/incidents.types';
import { IactiveLsStatusDto, IlogisticServiceDetails, TlogisticServiceStatus } from '../../types/services';
import {
  addIncidentLog,
  closeIncidentsBulk,
  createServiceIncident,
  getIncidentReasonsGrouped,
  getOpenIncidents
} from '../../services/incidents.service';
import { editActiveServiceInformation } from '../../services/services';
import { displayErrorNotification, displaySuccessNotification } from '../../utils/displayNudosStandardNotifications';
import { formatShortDate, formatShortDateAndHour } from '../../utils/formatDatesAndHours';
import { getNudosData } from '../../utils/getNudosData';

import './ServiceIncidentSidebar.scss';

const ACTIVE_STATUS: TlogisticServiceStatus = 'activo';

const buildDatesFromService = (service: IlogisticServiceDetails): IIncidentDatesFormValue => {
  const { estimatedDates, trackingLink, trackingCode, provider } = service;
  return {
    pickupDateFrom: estimatedDates?.collect?.since ?? '',
    pickupDateTo: estimatedDates?.collect?.until ?? '',
    deliveryDateFrom: estimatedDates?.delivery?.since ?? '',
    deliveryDateTo: estimatedDates?.delivery?.until ?? '',
    trackingLink: trackingLink ?? '',
    trackingCode: trackingCode ?? '',
    providerName: provider ?? ''
  };
};

const mapFormToActiveLsDto = (form: IIncidentDatesFormValue): IactiveLsStatusDto => ({
  collectSince: form.pickupDateFrom,
  collectUntil: form.pickupDateTo,
  deliverySince: form.deliveryDateFrom,
  deliveryUntil: form.deliveryDateTo,
  trackingLink: form.trackingLink,
  trackingCode: form.trackingCode,
  deliveryProviderName: form.providerName
});

const ServiceIncidentSidebar = ({
  open,
  mode,
  logisticServiceData,
  onClose,
  onActionCompleted
}: IServiceIncidentSidebar) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonsByType, setReasonsByType] = useState<IIncidentReasonsGroupedByType[]>([]);
  const [selectedReason, setSelectedReason] = useState<IIncidentReason>();
  const [isReasonComboOpen, setIsReasonComboOpen] = useState(false);
  const [openIncidents, setOpenIncidents] = useState<IOpenIncident[]>([]);
  const [serviceIncidents, setServiceIncidents] = useState<IOpenIncident[]>([]);
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [datesPayload, setDatesPayload] = useState<IIncidentDatesFormValue>({});
  const [initialDatesPayload, setInitialDatesPayload] = useState<IIncidentDatesFormValue>({});

  const isCreateMode = mode === INCIDENT_MODES.CREATE;
  const logisticServiceId = logisticServiceData?.id;
  const isActiveService = logisticServiceData?.statusName === ACTIVE_STATUS;
  const showsWizard = isCreateMode && isActiveService;
  const isCancelReason = !!selectedReason && CANCEL_INCIDENT_CODES.includes(selectedReason.code);
  const showsDatesStep = showsWizard && !isCancelReason;
  const slaStartDate = formatShortDate(logisticServiceData?.estimatedDates?.delivery?.since ?? undefined)?.onlyDate;
  const slaEndDate = formatShortDate(logisticServiceData?.estimatedDates?.delivery?.until ?? undefined)?.onlyDate;
  const hasModifiedDates = JSON.stringify(initialDatesPayload) !== JSON.stringify(datesPayload);

  const groupedReasons = useMemo(() => {
    return Object.keys(INCIDENT_REASON_TYPE_LABELS).flatMap(name => {
      const group = reasonsByType.find(g => g.name === name);
      if (!group || group.reasons.length === 0) return [];
      if (ACTIVE_ONLY_INCIDENT_GROUPS.has(name) && !isActiveService) return [];
      return [{ groupId: group.uuid, groupLabel: INCIDENT_REASON_TYPE_LABELS[name], items: group.reasons }];
    });
  }, [reasonsByType, isActiveService]);

  const closableIncidents: IBordClosableIncident[] = useMemo(() => {
    return openIncidents.map(incident => {
      const { activeLog } = incident;
      const { incidentReason, user, createdAt } = activeLog;
      const { onlyDateAndHour } = formatShortDateAndHour(createdAt) as { onlyDateAndHour: string };
      return {
        uid: String(incident.id),
        name: incidentReason.name,
        visibleId: incidentReason.code,
        createdAtLabel: onlyDateAndHour.toUpperCase(),
        createdBy: user ? `Creado por ${user.firstName} ${user.lastName}` : 'Creado por —'
      };
    });
  }, [openIncidents]);

  const openReasonCodes = useMemo(() => {
    return new Set(
      serviceIncidents
        .filter(incident => incident.activeLog.incidentStatusId === INCIDENT_STATUS_ID.OPEN)
        .map(incident => incident.activeLog.incidentReason.code)
    );
  }, [serviceIncidents]);

  const getReasonDisabled = useCallback(
    (reason: IIncidentReason): boolean => {
      if (!isActiveService) return false;
      const { code } = reason;
      const {
        DELIVERY_ATTEMPT_1, DELIVERY_ATTEMPT_2,
        PICKUP_ATTEMPT_1, PICKUP_ATTEMPT_2,
        CANCEL_DELIVERY, CANCEL_PICKUP
      } = INCIDENT_REASON_CODES;
      const hasDeliveryFamilyOpen = openReasonCodes.has(DELIVERY_ATTEMPT_1) || openReasonCodes.has(DELIVERY_ATTEMPT_2);
      const hasPickupFamilyOpen = openReasonCodes.has(PICKUP_ATTEMPT_1) || openReasonCodes.has(PICKUP_ATTEMPT_2);
      if (openReasonCodes.has(code)) return true;
      if (code === DELIVERY_ATTEMPT_1) return hasPickupFamilyOpen || openReasonCodes.has(DELIVERY_ATTEMPT_2);
      if (code === DELIVERY_ATTEMPT_2) return hasPickupFamilyOpen || !openReasonCodes.has(DELIVERY_ATTEMPT_1);
      if (code === PICKUP_ATTEMPT_1) return hasDeliveryFamilyOpen || openReasonCodes.has(PICKUP_ATTEMPT_2);
      if (code === PICKUP_ATTEMPT_2) return hasDeliveryFamilyOpen || !openReasonCodes.has(PICKUP_ATTEMPT_1);
      if (code === CANCEL_DELIVERY) return !openReasonCodes.has(DELIVERY_ATTEMPT_2);
      if (code === CANCEL_PICKUP) return !openReasonCodes.has(PICKUP_ATTEMPT_2);
      return false;
    },
    [isActiveService, openReasonCodes]
  );

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const findPredecessorIncidentId = (reasonCode: string): number | undefined => {
    const predecessorCodes = PREDECESSOR_CODES_BY_REASON[reasonCode];
    if (!predecessorCodes) return undefined;
    const predecessor = serviceIncidents.find(incident =>
      incident.activeLog.incidentStatusId === INCIDENT_STATUS_ID.OPEN
      && predecessorCodes.includes(incident.activeLog.incidentReason.code)
    );
    return predecessor?.id;
  };

  const handleCreate = async () => {
    if (!logisticServiceId || !selectedReason) return;
    const requiresLogPut = PREDECESSOR_CODES_BY_REASON[selectedReason.code] !== undefined;
    const predecessorIncidentId = requiresLogPut ? findPredecessorIncidentId(selectedReason.code) : undefined;
    if (requiresLogPut && !predecessorIncidentId) {
      displayErrorNotification({
        customJSXMessage: <>No se encontró el reporte previo necesario. Refresca y vuelve a intentar.</>
      });
      return;
    }
    setIsSubmitting(true);
    try {
      if (showsDatesStep && hasModifiedDates) {
        await editActiveServiceInformation(logisticServiceId, {
          ...mapFormToActiveLsDto(datesPayload),
          notifyDatesChanged: true
        });
      }
      if (predecessorIncidentId) {
        await addIncidentLog(predecessorIncidentId, { incidentReasonId: selectedReason.id });
      } else {
        await createServiceIncident({
          sourceId: logisticServiceId,
          incidentSourceReferenceId: INCIDENT_SOURCE_REFERENCE_ID.LOGISTIC_SERVICE,
          incidentReasonId: selectedReason.id,
          responsibleUserId: getNudosData()?.userData?.id
        });
      }
      displaySuccessNotification({
        customJSXMessage: <>Reporte del servicio creado y enviado con éxito.</>
      });
      onActionCompleted?.();
      onClose();
    } catch {
      displayErrorNotification();
    } finally {
      setIsSubmitting(false);
    }
  };

  const notifyCloseResult = (succeededCount: number, failed: ICloseIncidentsFailed[]) => {
    if (failed.length === 0) {
      const message = succeededCount === 1
        ? '1 reporte cerrado con éxito.'
        : `${succeededCount} reportes cerrados con éxito.`;
      displaySuccessNotification({ customJSXMessage: <>{message}</> });
      return;
    }

    const failedSummary = failed.map(item => `#${item.incidentId}: ${item.reason}`).join(' · ');

    if (succeededCount === 0) {
      displayErrorNotification({
        customJSXMessage: <>No se pudo cerrar ningún reporte. {failedSummary}</>
      });
      return;
    }

    displayErrorNotification({
      customJSXMessage: (
        <>
          Cerrados {succeededCount} de {succeededCount + failed.length}. Fallaron: {failedSummary}
        </>
      )
    });
  };

  const handleCloseIncidents = async () => {
    if (!logisticServiceId || selectedUids.length === 0) return;
    setIsSubmitting(true);
    try {
      const ids = selectedUids.map(Number);
      const { succeeded, failed } = await closeIncidentsBulk({ ids });
      notifyCloseResult(succeeded.length, failed);
      onActionCompleted?.();
      if (failed.length === 0) {
        onClose();
        return;
      }
      setSelectedUids([]);
      const refreshedIncidents = await getOpenIncidents(logisticServiceId, [INCIDENT_TYPE_ID.BLOCKING, INCIDENT_TYPE_ID.CANCELLED, INCIDENT_TYPE_ID.ATTEMPTS]);
      setOpenIncidents(refreshedIncidents);
    } catch {
      displayErrorNotification();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (showsDatesStep && currentStep === 1) {
      setCurrentStep(2);
      return;
    }
    if (isCreateMode) {
      handleCreate();
      return;
    }
    handleCloseIncidents();
  };

  const handleSecondaryAction = () => {
    if (showsWizard && currentStep === 2) {
      setCurrentStep(1);
      return;
    }
    handleClose();
  };

  const resetSidebarState = () => {
    setSelectedReason(undefined);
    setSelectedUids([]);
    setReasonsByType([]);
    setOpenIncidents([]);
    setServiceIncidents([]);
    setCurrentStep(1);
    setDatesPayload({});
    setInitialDatesPayload({});
    setIsSubmitting(false);
  };

  const loadSidebarData = () => {
    if (logisticServiceId == null) return;
    if (showsWizard) {
      const initial = buildDatesFromService(logisticServiceData);
      setInitialDatesPayload(initial);
      setDatesPayload(initial);
    }
    setIsLoading(true);
    const sidebarDataRequest = isCreateMode
      ? Promise.all([
          getIncidentReasonsGrouped().then(setReasonsByType),
          getOpenIncidents(logisticServiceId).then(setServiceIncidents)
        ])
      : getOpenIncidents(logisticServiceId, [INCIDENT_TYPE_ID.BLOCKING, INCIDENT_TYPE_ID.CANCELLED, INCIDENT_TYPE_ID.ATTEMPTS]).then(setOpenIncidents);
    sidebarDataRequest
      .catch(() => displayErrorNotification())
      .finally(() => setIsLoading(false));
  };

  const headerCopy = (() => {
    if (!isCreateMode) {
      return {
        title: 'Cerrar reportes',
        subtitle: 'Selecciona los reportes a cerrar. Se informará al cliente por correo.'
      };
    }
    if (showsWizard && currentStep === 2) {
      return {
        title: 'Paso 2: Actualiza las fechas estimadas de entrega (Opcional)',
        subtitle: 'Se informará de las nuevas fechas al cliente por correo electrónico'
      };
    }
    if (showsDatesStep) {
      return {
        title: 'Paso 1: Selecciona el motivo del reporte',
        subtitle: 'Elige el motivo del reporte. Se enviará al cliente por correo electrónico'
      };
    }
    return {
      title: 'Crear reporte del servicio',
      subtitle: 'Elige el motivo del reporte. Se enviará al cliente por correo electrónico.'
    };
  })();

  const primaryButtonLabel = (() => {
    if (!isCreateMode) {
      return selectedUids.length > 0 ? `Cerrar reportes (${selectedUids.length})` : 'Cerrar reportes';
    }
    if (showsDatesStep && currentStep === 1) return 'Continuar';
    return 'Crear reporte';
  })();

  const secondaryButtonLabel = showsWizard && currentStep === 2 ? 'Atrás' : 'Cancelar';
  const isPrimaryDisabled = isCreateMode ? !selectedReason : selectedUids.length === 0;

  useEffect(() => {
    if (!open) {
      resetSidebarState();
      return;
    }
    loadSidebarData();
  }, [open, isCreateMode, logisticServiceId]);

  useEffect(() => {
    setIsReasonComboOpen(open && isCreateMode);
  }, [open, isCreateMode]);

  const sidebarContent = isLoading ? (
    <div className="serviceIncidentSidebarLoading">Cargando información…</div>
  ) : !isCreateMode ? (
    <BordIncidentClosureList
      incidents={closableIncidents}
      selectedUids={selectedUids}
      onSelectionChange={setSelectedUids}
      selectAllLabel="Seleccionar todos los reportes abiertos"
      searchPlaceholder="Buscar..."
      emptyStateText="Este servicio no tiene reportes abiertos."
    />
  ) : showsWizard && currentStep === 2 ? (
    <>
      <BordIncidentDatesForm
        value={datesPayload}
        onChange={setDatesPayload}
        slaStartDate={slaStartDate}
        slaEndDate={slaEndDate}
      />
      {hasModifiedDates && (
        <div className="serviceIncidentSidebarDatesAlert">
          <BordOneToneIcon
            variant="alertTriangleWarning"
            standardSize={14}
            customClassName="serviceIncidentSidebarDatesAlertIcon"
          />
          <span>El cliente será informado por correo electrónico sobre el cambio de fechas.</span>
        </div>
      )}
    </>
  ) : (
    <>
      <BordGroupedComboBox<IIncidentReason>
        groups={groupedReasons}
        value={selectedReason ?? null}
        onChange={setSelectedReason}
        isOpen={isReasonComboOpen}
        onOpenChange={setIsReasonComboOpen}
        getOptionLabel={item => `${item.name} (ID: ${item.code})`}
        getOptionId={item => String(item.id)}
        getOptionKey={item => String(item.id)}
        getOptionDisabled={getReasonDisabled}
        renderOption={item => (
          <BordTooltip
            message={`${item.name} (ID: ${item.code})`}
            position="right"
            customStylesMessageContainer={{ zIndex: 1100 }}
          >
            <span className="serviceIncidentSidebarReasonOption">
              <span className="bordGroupedComboBoxDSoptionLabel">{item.name}</span>
              <span className="bordGroupedComboBoxDSoptionId">(ID: {item.code})</span>
            </span>
          </BordTooltip>
        )}
        placeholder="Selecciona o escribe el reporte"
        emptyStateText="No hay reportes registrados"
        loadingText="Cargando información"
      />
      {selectedReason?.imageUrl && (
        <BordEmailPreview
          imageUrl={selectedReason.imageUrl}
          imageAlt={`Vista previa del correo de ${selectedReason.name}`}
        />
      )}
    </>
  );

  return (
    <BordLateralModal
      open={open}
      onClose={handleClose}
      panelClassName="serviceIncidentSidebarPanel"
      bordLateralModalHeaderProps={{
        title: headerCopy.title,
        subtitle: headerCopy.subtitle,
        onClose: handleClose
      }}
      bordLateralModalContent={<div className="serviceIncidentSidebarBody">{sidebarContent}</div>}
      bordLateralModalFooterProps={{
        firstButton: {
          label: secondaryButtonLabel,
          modeButton: 'tertiary',
          bordButtonStyle: 'outlined',
          disabled: isSubmitting,
          onClick: handleSecondaryAction
        },
        secondButton: {
          label: primaryButtonLabel,
          modeButton: 'primary',
          bordButtonStyle: 'filled',
          disabled: isPrimaryDisabled,
          isLoading: isSubmitting,
          onClick: handlePrimaryAction
        }
      }}
    />
  );
};

export default ServiceIncidentSidebar;